import React, { useEffect, useRef, useCallback, useState } from 'react';
import { ApiReferenceReact } from '@scalar/api-reference-react';
import '@scalar/api-reference-react/style.css';
import moonSvg from '../../../static/assets/icons/toggle-moon.svg';
import sunSvg from '../../../static/assets/icons/toggle-sun.svg';
import GenerateDemoKeyModal from './GenerateDemoKeyModal';
import { colors } from '../../../static/constants/theme';

interface ApiReferenceTabProps {
  apiSpec: unknown;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
}

const TOGGLE_ID = 'scalar-theme-toggle';
const DEMO_KEY_BTN_ID = 'scalar-demo-key-btn';
const MENU_PROXY_ID = 'scalar-menu-proxy-btn';

const getToggleSvg = (currentTheme: 'light' | 'dark') => {
  const src = currentTheme === 'dark' ? moonSvg : sunSvg;
  return `<img src="${src}" alt="Toggle theme" width="36" height="24" />`;
};

const expandNestedSchemaFromHash = () => {
  const hash = decodeURIComponent(window.location.hash.slice(1));
  if (!hash) return;

  const schemaParamMatch = hash.match(/(.*)(\\.body\\.|\\.path\\.|\\.query\\.|\\.header\\.)(.*)/);
  if (!schemaParamMatch) return;

  const nestedParam = schemaParamMatch[3];
  if (!nestedParam) return;

  const existingTarget = document.querySelector(`[id*="${nestedParam}"]`);
  if (existingTarget) return;

  const disclosureButtons = document.querySelectorAll('.schema-card-title--compact');
  for (const btn of disclosureButtons) {
    const el = btn as HTMLElement;
    if (el.textContent?.includes('Show additional properties') && el.offsetParent !== null) {
      el.click();
    }
  }

  const tryScrollToTarget = (attempts: number) => {
    if (attempts <= 0) return;

    const allIds = document.querySelectorAll(`[id*="${nestedParam}"]`);
    const target = [...allIds].find((el) => {
      const id = el.getAttribute('id') || '';
      return id.endsWith(nestedParam) || id.includes(`.${nestedParam}`);
    });

    if (target) {
      target.scrollIntoView({ block: 'start' });
    } else {
      requestAnimationFrame(() => tryScrollToTarget(attempts - 1));
    }
  };

  setTimeout(() => tryScrollToTarget(30), 200);
};

const ApiReferenceTab = ({ apiSpec, theme, onToggleTheme }: ApiReferenceTabProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onToggleTheme);
  const themeRef = useRef(theme);
  callbackRef.current = onToggleTheme;
  themeRef.current = theme;

  const [modalOpen, setModalOpen] = useState(false);
  const setModalOpenRef = useRef(setModalOpen);
  setModalOpenRef.current = setModalOpen;
  const isDark = theme === 'dark';

  const handleLoaded = useCallback(() => {
    setTimeout(expandNestedSchemaFromHash, 500);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    let observer: MutationObserver | null = null;

    const inject = () => {
      if (!container) return;

      // Inject theme toggle
      const headerSlot = container.querySelector('.t-doc__header .flex.h-6');
      if (headerSlot && !container.querySelector(`#${TOGGLE_ID}`)) {
        observer?.disconnect();
        const btn = document.createElement('button');
        btn.id = TOGGLE_ID;
        btn.type = 'button';
        btn.setAttribute('aria-label', 'Toggle theme');
        btn.style.cssText = 'background:none;border:none;padding:0;cursor:pointer;display:flex;align-items:center;';
        btn.innerHTML = getToggleSvg(themeRef.current);
        btn.addEventListener('click', () => callbackRef.current());
        headerSlot.appendChild(btn);
        observer?.observe(container, { childList: true, subtree: true });
      }

      // Override auth input placeholder (handles both native inputs and CodeMirror spans)
      const PLACEHOLDER_LABEL = 'Enter your API key';
      const BASE64_PLACEHOLDER = 'QUxMIFlPVVIgQkFTRSBBUkUgQkVMT05HIFRPIFVT';
      for (const el of container.querySelectorAll<HTMLInputElement>('input[type="password"], input[type="text"]')) {
        if (el.placeholder === BASE64_PLACEHOLDER) el.placeholder = PLACEHOLDER_LABEL;
      }
      for (const el of container.querySelectorAll('.cm-placeholder')) {
        if (el.textContent === BASE64_PLACEHOLDER) el.textContent = PLACEHOLDER_LABEL;
      }

      // Replace Scalar default hamburger menu button with MUI-styled one
      const scalarMenuBtns = container.querySelectorAll('button');
      for (const btn of scalarMenuBtns) {
        const srOnly = btn.querySelector('span.sr-only');
        if (srOnly && srOnly.textContent === 'Open Menu' && !container.querySelector(`#${MENU_PROXY_ID}`)) {
          // Hide original button but keep in DOM so Scalar's click handler stays intact
          btn.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;opacity:0;';

          // Create visible proxy button that forwards clicks to the hidden original
          const proxyBtn = document.createElement('button');
          proxyBtn.id = MENU_PROXY_ID;
          proxyBtn.type = 'button';
          proxyBtn.setAttribute('aria-label', 'Open sidebar');
          proxyBtn.style.cssText = 'background:none;border:none;padding:8px;cursor:pointer;display:inline-flex;align-items:center;border-radius:50%;';
          proxyBtn.innerHTML = `<svg focusable="false" aria-hidden="true" viewBox="0 0 24 24" style="width:24px;height:24px;fill:currentColor;"><path d="M3 18h18v-2H3zm0-5h18v-2H3zm0-7v2h18V6z"></path></svg>`;
          proxyBtn.style.color = themeRef.current === 'light' ? colors.text.primary : colors.white;
          proxyBtn.addEventListener('click', () => btn.click());
          btn.parentElement?.insertBefore(proxyBtn, btn);
        }
      }

      // Inject Generate Demo Key button after the auth section
      const authSection = container.querySelector('.scalar-reference-intro-auth');
      if (authSection && !container.querySelector(`#${DEMO_KEY_BTN_ID}`)) {
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'padding:12px 0 4px 0;';
        const demoBtn = document.createElement('button');
        demoBtn.id = DEMO_KEY_BTN_ID;
        demoBtn.type = 'button';
        demoBtn.textContent = 'Generate Demo Key';
        demoBtn.className = 'btn btn--signup';
        demoBtn.style.cssText = 'white-space:nowrap;';
        demoBtn.addEventListener('click', () => setModalOpenRef.current(true));
        wrapper.appendChild(demoBtn);
        authSection.after(wrapper);
      }
    };

    inject();
    observer = new MutationObserver(inject);
    if (container) observer.observe(container, { childList: true, subtree: true });

    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    const btn = containerRef.current?.querySelector(`#${TOGGLE_ID}`);
    if (btn) btn.innerHTML = getToggleSvg(theme);

    // Update menu proxy button color on theme change
    const menuBtn = containerRef.current?.querySelector(`#${MENU_PROXY_ID}`) as HTMLElement | null;
    if (menuBtn) {
      menuBtn.style.color = theme === 'light' ? colors.text.primary : colors.white;
    }
  }, [theme]);

  useEffect(() => {
    const onHashChange = () => {
      setTimeout(expandNestedSchemaFromHash, 300);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return (
    <div
      ref={containerRef}
      data-theme={theme}
      className={isDark ? 'dark-mode' : 'light-mode'}
      style={{ position: 'relative' }}
    >
      <ApiReferenceReact
        configuration={{
          spec: { content: apiSpec },
          hideClientButton: true,
          hideModels: true,
          hideDarkModeToggle: true,
          showDeveloperTools: 'never',
          layout: 'modern',
          theme: 'default',
          onLoaded: handleLoaded,
          agent: {
            disabled: true,
          },
          authentication: {
            preferredSecurityScheme: 'apiKey',
          },
          customCss: `
          .ascii-art { display: none !important; }
          .darklight-reference { display: none !important; }
          .scalar-reference-intro-clients { display: none !important; }
          [id$="-group-2"] { display: none !important; }
          .light-mode {
            --scalar-color-1: #000C30;
            --scalar-color-2: rgba(17, 29, 66, 0.9);
            --scalar-color-3: rgba(0, 12, 48, 0.75);
            --scalar-color-accent: #519EE6;
            --scalar-background-1: #FFFFFF;
            --scalar-background-2: #EFF3FF;
            --scalar-background-3: #F7F9FF;
            --scalar-background-accent: #EFF3FF;
            --scalar-border-color: #C3CAE2;
          }
          .dark-mode {
            --scalar-color-1: #EFF3FF;
            --scalar-color-2: rgba(239, 243, 255, 0.9);
            --scalar-color-3: #BBD5EA;
            --scalar-color-accent: #8ab4f8;
            --scalar-background-1: #151823;
            --scalar-background-2: #191F31;
            --scalar-background-3: #151823;
            --scalar-background-accent: #2F3444;
            --scalar-border-color: #363C4D;
          }
          .light-mode label,
          .light-mode .scalar-card-header,
          .light-mode .scalar-card-header-title {
            color: var(--scalar-color-1) !important;
          }
          .dark-mode label,
          .dark-mode .scalar-card-header,
          .dark-mode .scalar-card-header-title {
            color: var(--scalar-color-1) !important;
          }
        `,
        }}
      />
      <GenerateDemoKeyModal open={modalOpen} onClose={() => setModalOpen(false)} theme={theme} />
    </div>
  );
};

export default ApiReferenceTab;
